import { TeamSwitcher } from '@askable/ui/components/team-switcher';
import { Avatar, AvatarFallback } from '@askable/ui/core/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuPortal,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@askable/ui/core/dropdown-menu';
import { toast } from '@askable/ui/core/sonner';
import { supportedLanguages } from '@askableorg/i18n';
import { Languages } from 'lucide-react';
import Link from 'next/link';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'urql';

import { UpdateUserLanguage } from 'src/queries/updateme.mutation';
import { toEnumValue } from 'src/utils/enum-utils';
import { setLangCookie } from 'src/utils/lang-utils';

import { useViewerContext } from '@/context/ViewerContext/ViewerContext';
import { Locale as GraphQLLocale } from '@/constants/graphql-enums';
import { useIsKinde } from '@/hooks/useIsKinde/useIsKinde';
import { useHandleSaveError } from '@/hooks/useHandleSaveError/useHandleSaveError';

import type { Viewer } from '@/context/ViewerContext/data/Viewer.query';
import type { Locale } from '@askableorg/i18n';
import type { ResultOf } from 'gql.tada';

type Teams = NonNullable<NonNullable<NonNullable<ResultOf<typeof Viewer>['viewer']>['Teams']>[number]>;

export const UserMenu = () => {
  const { i18n } = useTranslation();
  const [, updateUserLanguage] = useMutation(UpdateUserLanguage);
  const handleSaveError = useHandleSaveError();

  const changeLanguageHandler = (lang: Locale) => {
    const graphqlLocale = toEnumValue(GraphQLLocale, lang.replaceAll('-', '_'));
    // TODO: if graphqlLocale is undefined, we silently skip the server update
    // but still apply i18n + cookie locally — server desyncs on next login.
    // Realistically only reachable for locales missing from the GraphQL enum
    // (e.g. hu/cs/sk behind feature flag). Decide whether to block the change
    // or surface a (translated) warning.
    if (graphqlLocale) {
      void updateUserLanguage({
        user: {
          settings: {
            language: graphqlLocale,
          },
        },
      }).then(handleSaveError);
    }

    void i18n.changeLanguage(lang);

    if (typeof window === 'undefined') {
      return;
    }

    // Save selected language to a cookie to share with Clients
    setLangCookie(lang);
  };

  const { details: viewer } = useViewerContext();
  const isKinde = useIsKinde();

  const handleChange = async (id: string) => {
    const selectedTeam = viewer?.Teams?.find(team => team?._id === id);

    if (!selectedTeam?.kinde_org_id) {
      toast.error(`${selectedTeam?.name ?? 'The selected team'} has not been migrated. Please contact support.`);
      return;
    }

    const params = new URLSearchParams();
    params.append('org_code', selectedTeam?.kinde_org_id!);
    params.append('post_login_redirect_url', `${window.location.origin}/${window.location.pathname?.split('/')[1]}`);
    window.location.replace(`${window.location.origin}/api/auth/login?${params.toString()}`);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild className="cursor-pointer">
        <Avatar>
          <AvatarFallback>{viewer?.meta?.identity?.firstname?.[0] ?? 'U'}</AvatarFallback>
        </Avatar>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" side="bottom" align="end">
        {isKinde && (
          <>
            {viewer?.ConnectedTeam?.name ? <DropdownMenuLabel>{viewer?.ConnectedTeam?.name}</DropdownMenuLabel> : null}
            <TeamSwitcher
              handleSwitchTeams={id => {
                void handleChange(id);
              }}
              activeOrgId={viewer?.ConnectedTeam?._id}
              teams={viewer?.Teams as Pick<Teams, '_id' | 'name'>[]}
              hideSignIntoAnotherTeam
            />
          </>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className="gap-2">
            <Languages className="h-4 w-4" />
            <span>{supportedLanguages.find(l => l.code === i18n.language)?.label}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent>
              {supportedLanguages.map(lang => (
                <DropdownMenuItem
                  className={i18n.language === lang.code ? 'font-bold' : undefined}
                  key={lang.code}
                  onClick={() => changeLanguageHandler(lang.code)}
                >
                  {lang.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>
        <DropdownMenuSeparator />
        <SignOutDropdownMenuItem isKinde={isKinde} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

const SignOutDropdownMenuItem = ({ isKinde }: { isKinde: boolean }) => {
  const { t } = useTranslation();

  const signOutText = t('global.signOut');

  if (isKinde) {
    return (
      <DropdownMenuItem asChild>
        <Link href="/api/auth/logout">{signOutText}</Link>
      </DropdownMenuItem>
    );
  }

  return (
    <DropdownMenuItem
      asChild
      onClick={() => {
        if (typeof window === 'undefined') {
          return;
        }

        window.localStorage.clear();
      }}
    >
      <span>{signOutText}</span>
    </DropdownMenuItem>
  );
};
